import { type UseNavigateResult, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

type OpenMaskDialogOptions = {
  /** Unmasked route the app actually navigates to (where the overlay is mounted). */
  to: string;
  action: string;
  /**
   * Clean URL shown in the address bar. A shared link loads it (a reload keeps the unmasked location, which the
   * history entry holds), so it must open the same overlay: a shim route that redirects to `to` (`MaskRedirect`), or a
   * real route that opens it from its own search.
   */
  mask: string;
  /** Search params of the masked URL (e.g. the record id a real route reads), so the address bar names what is open. */
  maskSearch?: Record<string, unknown>;
  /** Overlay state carried on the mask contract's `prefill` key (see `maskSearchFragment`). */
  prefill?: Record<string, unknown>;
  /**
   * Pass `true` when the opener already sits on `to` and the overlay's open state lives outside
   * the URL (a store) — otherwise the extra history entry's back-step drops the mask while the
   * overlay stays open. An overlay that `action` alone opens pushes: Back then closes it.
   */
  replace?: boolean;
};

// to/mask are caller-supplied literals, not resolvable at module type-level.
// Cast matches `MaskRedirect`'s `NavigateAny` and `MaskLink`'s `mask as never`.
type NavigateAny = (opts: {
  to: string;
  search: (prev: Record<string, unknown>) => Record<string, unknown>;
  mask: { to: string; search?: Record<string, unknown> };
  replace?: boolean;
}) => Promise<void>;

/**
 * Opens a masked overlay with the router's `navigate`: what `useOpenMaskDialog` does, for code that is handed
 * `navigate` rather than calling hooks (a host's link handler).
 */
export function openMaskDialog(
  navigate: UseNavigateResult<string>,
  { to, action, mask, maskSearch, prefill, replace }: OpenMaskDialogOptions,
): Promise<void> {
  return (navigate as unknown as NavigateAny)({
    to,
    search: (prev) => ({
      ...prev,
      action,
      ...(prefill !== undefined ? { prefill } : {}),
    }),
    mask: { to: mask, ...(maskSearch ? { search: maskSearch } : {}) },
    ...(replace ? { replace } : {}),
  });
}

/**
 * Imperative twin of `MaskLink`, for openers that are button handlers rather than links
 * (a tile CTA that also writes to a store). Closing stays `useCloseMaskDialog`.
 */
export function useOpenMaskDialog() {
  const navigate = useNavigate();

  return useCallback(
    (options: OpenMaskDialogOptions) => openMaskDialog(navigate, options),
    [navigate],
  );
}
